import styled from '@/styles';
import type { TierBoundaryLite } from '@/services/workersV2API';
import { tierColor } from '@/routes/Agency/WorkerV2/components/dependabilityV2/tierColors';
import { tierRangeLabel } from '../mappings';

// GRAVY-10834: the design legend (Figma 38010:24790) is four 80px columns,
// each a 24px medal over a continuous 4px bar with rounded outer ends, the
// name and range right-aligned beneath; no gap between columns.
const Row = styled('div', { display: 'flex' });
const Col = styled('div', {
  width: '80px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: '4px',
  fontSize: '12px',
});
const MedalRow = styled('div', {
  display: 'flex',
  alignItems: 'center',
  paddingBottom: '4px',
});
const Bar = styled('div', {
  alignSelf: 'stretch',
  height: '4px',
  variants: {
    end: {
      first: { borderRadius: '100px 0 0 100px' },
      last: { borderRadius: '0 100px 100px 0' },
      middle: {},
    },
  },
});
const Text = styled('div', { paddingRight: '4px', textAlign: 'right' });

// Same medal path TierPill draws (Figma 37920:11819), in the tier color.
const Medal = ({ color }: { color: string }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    data-testid="tierLegendMedal"
  >
    <path
      d="M15.1266 4.00024C15.1266 8.2402 13.6066 13.2005 8.16666 15.2004C2.66279 13.1525 1.49951 6.88034 1.60611 4.00024L8.16666 0.800049L15.1266 4.00024ZM7.02994 6.22388L4.06021 6.61548L6.23306 8.67798L5.68716 11.6243L8.32095 10.1956L10.9538 11.6243L10.4088 8.67798L12.5817 6.61548L9.61099 6.22388L8.32095 3.51978L7.02994 6.22388Z"
      fill={color}
    />
  </svg>
);
const Name = styled('div', { fontWeight: 600, color: '#1C1C1C' });
const Range = styled('div', { color: '#5B5B5B', fontSize: '11px' });

const TierLegend = ({ boundaries }: { boundaries: TierBoundaryLite[] }) => {
  if (boundaries.length === 0) return <p>Tier data is unavailable.</p>;
  return (
    <Row data-testid="tierLegend">
      {boundaries.map((b, i) => (
        <Col key={b.tier} data-testid={`tierLegend-${b.tier}`}>
          <MedalRow>
            <Medal color={tierColor(b.tier)} />
          </MedalRow>
          <Bar
            end={
              i === 0
                ? 'first'
                : i === boundaries.length - 1
                  ? 'last'
                  : 'middle'
            }
            style={{ backgroundColor: tierColor(b.tier) }}
          />
          <Text>
            <Name>{b.label}</Name>
            <Range>{tierRangeLabel(b, i, boundaries.length)}</Range>
          </Text>
        </Col>
      ))}
    </Row>
  );
};

export default TierLegend;
